import { useEffect, useState } from 'react'
import { Button, message } from 'components/base'
import { Card, Modal, Skeleton } from 'antd'
import { IActionsPanelProps } from 'components/shared/actions-panel'
import { useHistory, useParams } from 'react-router-dom'
import { useProjectQuery } from '@timenotes/shared/src/services/projects/projects.hooks'
import { useTopNavigationLinks } from 'hooks/layout'
import useApiClient from 'hooks/useApiClient'
import ProjectDetailsHeader from '../shared/components/project-details-header'
import LoadingSkeleton from './components/loading-skeleton'
import TasksTable from './components/tasks-table/tasks-table'
import TaskForm from './components/task-form'
import { useTasksFiltersQueryParams } from "../shared/hooks/use-tasks-filters-query-params"
import { usePaginatedTasksQuery } from "@timenotes/shared/src/services/tasks/queries/use-paginated-tasks-query"
import useNoInitialEffect from "hooks/useNoInitialEffect"
import {
  ICreateTaskParams,
  TApiTasksFilters,
  TApiTasksSorting,
} from "@timenotes/shared/src/services/api-client/tasks-api"
import { TApiProject, TApiTask } from "@timenotes/shared/src/services/api-client/types"
import { getApiErrorMessage } from "@timenotes/shared/src/services/api-client"
import { useQueryClient } from 'react-query'
import SectionTasksTable from './components/section-tasks-table'
import { useProjectSectionsQuery } from '@timenotes/shared/src/services/project-sections/queries/use-project-sections-query'
import TasksFiltersContainer from '../shared/tasks-filters-container'
import { useProjectBoard, withProjectBoard } from '@timenotes/shared/src/features/project-board'
import useSearchParams from 'hooks/use-search-params'
import { DEFAULT_TASKS_FILTERS } from '@timenotes/shared/src/services/tasks/hooks/use-tasks-filters-formik'
import DraggableSections, { SortableSection } from './draggable-sections'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { UniqueIdentifier } from '@dnd-kit/core'
import { useUpdateTaskSectionMutation } from '@timenotes/shared/src/services/tasks/queries/use-update-task-section-mutation'
import { useUpdateTaskPositionMutation } from '@timenotes/shared/src/services/tasks/queries/use-update-task-position-mutation'
import AddSectionContainer from '../board/components/add-section-container'

// wrapping the whole page into project board feature
// in order to be able to play with the project board hook on the page level
const ProjectsTasksPage = withProjectBoard(() => {
  const history = useHistory()
  const apiClient = useApiClient()

  const params = useParams<{
    projectId: string
  }>()

  const projectId = params.projectId as string
  const queryParams = useTasksFiltersQueryParams()

  const [pageLoaded, setPageLoaded] = useState(false)
  const [newTaskModalVisible, setNewTaskModalVisible] = useState(false)

  useTopNavigationLinks([
    {
      label: "← BACK",
      onClick: () => {
        history.push("/projects")
      },
    },
  ])

  const projectQuery = useProjectQuery({ projectId: projectId })
  const project = projectQuery.data?.project

  // option to bind tasks filters to query params easily
  const {
    tasksFiltersFormik,
    isDraggingSection,
  } = useProjectBoard()

 
  const {
    projectSectionsQuery,
  } = useProjectSectionsQuery({
    projectId,
    filters: tasksFiltersFormik.values
  }, {
    refetchOnWindowFocus: true, // just confirm refetch needed
    staleTime: 10 * 60 * 1000 // 10 minutes
  })

  const queryClient = useQueryClient()
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(['project-sections', projectId, tasksFiltersFormik.values])
    }
  }, [])

  const [searchParams, setSearchParams] = useSearchParams<TApiTasksFilters>(undefined, DEFAULT_TASKS_FILTERS)

  useEffect(() => {
    tasksFiltersFormik.setValues(searchParams)
  }, [])

  useNoInitialEffect(() => {
    setSearchParams({
      ...tasksFiltersFormik.values,
      // special case for all being undefined, but not default value we want to send in filters API request 
      isBillable: tasksFiltersFormik.values.isBillable === undefined ? undefined : tasksFiltersFormik.values.isBillable, // TODO: this should not be needed?
      state: tasksFiltersFormik.values.state === undefined ? 'all' : tasksFiltersFormik.values.state,
    })
  }, [JSON.stringify(tasksFiltersFormik.values)])

  const projectSections = projectSectionsQuery.data?.projectSections || []

  const updateTaskSectionMutation = useUpdateTaskSectionMutation({
    projectId: projectId,
    filters: tasksFiltersFormik.values,
  })

  const updateTaskPositionMutation = useUpdateTaskPositionMutation({
    projectId: projectId,
    filters: tasksFiltersFormik.values,
  })

  return (
    <>
      <ProjectDetailsHeader 
        projectId={projectId} 
        activeSection="tasks" 
      />

      <div className="page-content">
        <TasksFiltersContainer 
          projectId={projectId}
        />

        {projectSectionsQuery.isLoading && (
          <Card>
            <Skeleton />
          </Card>
        )}

        {projectSectionsQuery.isSuccess && (
          <>
            <DraggableSections
              projectId={projectId}
              projectSections={projectSections}
              onTaskPositionChange={(params) => {
                //message.success(JSON.stringify(params))
                // message.success(params.projectSectionPosition)
                updateTaskPositionMutation.mutate(params)
              }}

              onTaskSectionChange={(params) => {
                updateTaskSectionMutation.mutate({
                  position: 0,
                  ...params
                })
              }}
            >
              <SortableContext
                items={projectSections.map((ps) => ps.id) as UniqueIdentifier[]}
                strategy={verticalListSortingStrategy}
              >
                {projectSections.map((projectSection) => (
                  <SortableSection
                    key={projectSection.id}
                    id={projectSection.id}
                  >
                    <SectionTasksTable
                      key={projectSection.id}
                      forceCollapse={isDraggingSection}
                      project={project as TApiProject}
                      projectSection={projectSection}
                      filters={tasksFiltersFormik.values}
                    />
                  </SortableSection>
                ))}
              </SortableContext>
            </DraggableSections>

            <AddSectionContainer
              projectId={projectId}
            />

            <br />
            <br />
            <br />
            <br />
          </>
        )}
      </div>
    </>
  )
})

export default ProjectsTasksPage
